import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { QUICKFLOWS_SINGLE_SECURITY_COOKIE_NAME } from 'QuickflowsApp/ts/constants';
import {
    QuickflowRedirectResponse,
    QuickflowSecurityInfo,
    SingleSecurityQuickflowCollectionResponse,
} from 'QuickflowsApp/ts/interfaces/quickflow-view-responses.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { CookieService } from 'ngx-cookie-service';
import { QuickflowsService } from 'QuickflowsApp/ts/services/quickflows.service';

import { IconSearchAutocompleteComponent } from 'SearchApp/ts/features/icon-search-autocomplete/icon-search-autocomplete.component';

/**
 *
 * Single Select Quickflows Component
 *
 * This component is one tab of the quickflows menu.
 *
 * Input Properties:
 *   displaySecurityId: The display security ID to initialize this component with, e.g. VTSAX
 *   securityId: The security ID to initialize this component with, e.g. M:VTSAX
 *
 */

@Component({
    standalone: false,
    selector: 'ycn-single-select-quickflows',
    templateUrl: './single-select-quickflows.template.html',
})
export class SingleSelectQuickflowsComponent implements OnInit {
    @Input() displaySecurityId?: string;

    @Input() securityId?: string;

    @Input() securityName?: string;

    @Input() securityUrl?: string;

    @ViewChild(IconSearchAutocompleteComponent) autocompleteComponent!: IconSearchAutocompleteComponent;

    availableQuickflows: SingleSecurityQuickflowCollectionResponse[] = [];

    currentSecurity: QuickflowSecurityInfo | null = null;

    loadingCollection: boolean = false;

    constructor(private cookieService: CookieService, private quickflowsService: QuickflowsService) {}

    ngOnInit(): void {
        // autocompleter uses `search_id` which is essentially a security's `security_id`
        if (this.securityId && this.displaySecurityId && this.securityName && this.securityUrl) {
            this.addItem({
                search_id: this.securityId,
                display_security_id: this.displaySecurityId,
                name: this.securityName,
                url: this.securityUrl,
            });
        } else {
            const cookie = this.cookieService.get(QUICKFLOWS_SINGLE_SECURITY_COOKIE_NAME) || '""';
            const security = JSON.parse(decodeURIComponent(cookie));

            // special case for when hover feature is first launched; cookies not storing `securityName`
            // will have `name` set to an empty string
            let securityName = '';
            if (security.securityName) {
                // eslint-disable-next-line prefer-destructuring
                securityName = security.securityName;
            }

            // special case for when hover feature is first launched; cookies not storing `securityUrl`
            // will have `url` set to the current URL
            let securityUrl = window.location.pathname;
            if (security.securityUrl) {
                // eslint-disable-next-line prefer-destructuring
                securityUrl = security.securityUrl;
            }

            if (security && Object.keys(security).length > 0) {
                this.addItem({
                    search_id: security.securityId,
                    display_security_id: security.displaySecurityId,
                    name: securityName,
                    url: securityUrl,
                });
            }
        }
    }

    select(quickflowId: string): void {
        this.quickflowRedirect(quickflowId);
    }

    quickflowRedirect(quickflowId: string): void {
        if (!this.currentSecurity) {
            return;
        }

        this.quickflowsService
            .getSingleSecurityQuickflowsRedirect(quickflowId, this.currentSecurity.securityId)
            .subscribe((data: QuickflowRedirectResponse) => {
                const quickflowURL = new URL(data.url);
                // Change the browser URL to the quickflows URL.
                window.location.assign(quickflowURL.href);
                // If the path of the application is not changing we need to set the
                // url state and reload so that the application loads from scratch.
                if (quickflowURL.pathname === window.location.pathname) {
                    window.location.reload();
                }
            });
    }

    addItem(object: Pick<AutocompleteResult, 'display_security_id' | 'search_id' | 'name' | 'url'>): void {
        // autocompleter uses `search_id` which is essentially a security's `security_id`
        this.currentSecurity = {
            displaySecurityId: object.display_security_id,
            securityId: object.search_id,
            securityName: object.name,
            securityUrl: object.url,
        };

        this.cookieService.set(
            QUICKFLOWS_SINGLE_SECURITY_COOKIE_NAME,
            encodeURIComponent(JSON.stringify(this.currentSecurity)),
            {
                path: '/',
            }
        );

        this.updateQuickflowCollection();
        this.focusAutocompleter();
    }

    deleteItem(): void {
        this.availableQuickflows = [];
        this.currentSecurity = null;
        this.cookieService.set(QUICKFLOWS_SINGLE_SECURITY_COOKIE_NAME, '', { path: '/' });
        this.focusAutocompleter();
    }

    updateQuickflowCollection(): void {
        if (!this.currentSecurity) {
            return;
        }

        this.loadingCollection = true;

        // fetch all single security quickflows
        this.quickflowsService
            .getSingleSecurityQuickflowsCollection(this.currentSecurity.securityId)
            .pipe(
                finalize(() => {
                    this.loadingCollection = false;
                })
            )
            .subscribe({
                next: (data: SingleSecurityQuickflowCollectionResponse[]) => {
                    this.availableQuickflows = data.filter((quickflowCategory) => {
                        const categoryName = Object.keys(quickflowCategory)[0];
                        return quickflowCategory[categoryName].length > 0;
                    });
                },
                error: () => {
                    this.availableQuickflows = [];
                },
            });
    }

    public focusAutocompleter(): void {
        if (this.autocompleteComponent) {
            this.autocompleteComponent.focus();
        }
    }
}
